/*
 * @Author: Mr.mjc
 * @Date: 2022-06-27 19:02:37
 * @LastEditors: MJC
 * @LastEditTime: 2025-10-23 10:21:54
 * @Description:
 * @FilePath: /next-ui/packages/wui-table/src/DragResizerLine.tsx
 */
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { Event } from "./lib/utils";
import { IDragResizerLineProps } from './iTable';

// 拖拽的尺表线
// eslint-disable-next-line no-spaced-func
const DragResizerLine = forwardRef<{ start:(event: React.MouseEvent<HTMLElement>) => void }, IDragResizerLineProps>((props, ref) => {
    const {
        clsPrefix = '',
        container = null,
        visible = false,
        left: propLeft = null,
        height = null
    } = props;

    const [left, setLeft] = useState(propLeft);
    const [x, setX] = useState(0);
    const [moveX, setMoveX] = useState(0);

    // 更新left状态当props.left变化时
    useEffect(() => {
        if ('left' in props && propLeft !== left) {
            setLeft(propLeft);
        }
    }, [propLeft, props]);

    // 使用ref来存储事件处理函数需要的值
    const propsRef = useRef<IDragResizerLineProps>();
    propsRef.current = props;

    const stateRef = useRef({ x: 0, moveX: 0 });

    // 同步state到ref
    useEffect(() => {
        stateRef.current = { x, moveX };
    }, [x, moveX]);

    // 事件处理函数，使用ref确保它们不会变化
    const eventHandlersRef = useRef<{
        handleResizerMove:(event: React.MouseEvent<HTMLElement>) => void;
        handleResizerDown: (event: React.MouseEvent<HTMLElement>) => void;
        handleResizerUp: (event: React.MouseEvent<HTMLElement>) => void;
        handleResizerCancel: () => void;
    }>();

    if (!eventHandlersRef.current) {
        eventHandlersRef.current = {
            handleResizerMove: (event: React.MouseEvent<HTMLElement>) => {
                const currentProps = propsRef.current!;
                if (currentProps.visible) {
                    const state = stateRef.current;
                    let newmoveX = state.moveX + (event.clientX - state.x);

                    // 应用限制
                    if (currentProps.dragborder !== 'fixed') {
                        if (currentProps.defaultWidth && currentProps.minWidth &&
                            currentProps.defaultWidth + newmoveX < currentProps.minWidth) {
                            newmoveX = currentProps.minWidth - currentProps.defaultWidth;
                        }
                    } else {
                        if (currentProps.defaultWidth && currentProps.minWidth &&
                            currentProps.defaultWidth + newmoveX < currentProps.minWidth) {
                            newmoveX = currentProps.minWidth - currentProps.defaultWidth;
                        }
                        if (currentProps.defaultWidth && currentProps.maxWidth &&
                            currentProps.defaultWidth + newmoveX > currentProps.maxWidth) {
                            newmoveX = currentProps.maxWidth - currentProps.defaultWidth;
                        }
                    }

                    // 更新状态
                    setLeft((currentProps.left || 0) + newmoveX);
                    setX(event.clientX);
                    setMoveX(newmoveX);

                    // 触发onChange回调
                    currentProps.onChange && currentProps.onChange(event, state.moveX, currentProps.dataSource);
                    Event.stopPropagation(event);
                }
            },
            handleResizerDown: (event: React.MouseEvent<HTMLElement>) => {
                const currentProps = propsRef.current!;
                if (currentProps.visible) {
                    setMoveX(0);
                    setX(event.clientX);
                    Event.stopPropagation(event);
                }
            },
            handleResizerUp: (event: React.MouseEvent<HTMLElement>) => {
                const currentProps = propsRef.current!;
                const state = stateRef.current;
                if (currentProps.visible) {
                    currentProps.onResizeEnd && currentProps.onResizeEnd(event, state.moveX, currentProps.dataSource);
                }
            },
            handleResizerCancel: () => {
                const currentProps = propsRef.current!;
                if (currentProps.visible) {
                    currentProps.onResizeCancel && currentProps.onResizeCancel();
                }
            }
        };
    }

    // 组件挂载和更新时处理事件
    useEffect(() => {
        if (container) {
            const domElem = document.querySelector(`div[data-for-table="${container}"]`) as HTMLElement;
            if (!domElem) return;

            const handlers = eventHandlersRef.current!;
            Event.addHandler(domElem, 'mousedown', handlers.handleResizerDown);
            Event.addHandler(domElem, 'mousemove', handlers.handleResizerMove);
            Event.addHandler(domElem, 'mouseup', handlers.handleResizerUp);
            if (domElem !== document.body) {
                Event.addHandler(document.body, 'mouseup', handlers.handleResizerCancel);
            }

            return () => {
                Event.removeHandler(domElem, 'mousedown', handlers.handleResizerDown);
                Event.removeHandler(domElem, 'mousemove', handlers.handleResizerMove);
                Event.removeHandler(domElem, 'mouseup', handlers.handleResizerUp);
                if (domElem !== document.body) {
                    Event.removeHandler(document.body, 'mouseup', handlers.handleResizerCancel);
                }
            };
        }
    }, [container]);

    // 暴露start方法给父组件
    useImperativeHandle(ref, () => ({
        start: (event: React.MouseEvent<HTMLElement>) => {
            setMoveX(0);
            setX(event.clientX);
            Event.stopPropagation(event);
        }
    }), []);

    const style = { left: left, height: height, display: visible ? 'block' : 'none' };
    return (
        <div style={style as React.CSSProperties} className={`${clsPrefix}-drag-resizer`}>
            <div className={`${clsPrefix}-drag-resizer-line`}></div>
        </div>
    );
});

DragResizerLine.displayName = 'DragResizerLine';

export default DragResizerLine;